import { z } from 'zod';

import type { LocationPin } from './types';

const startPinSchema = z.object({
  postId: z.string().min(1),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180)
});

const pinsArraySchema = z.array(
  z.object({
    postId: z.string().guid(),
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180)
  })
);

/**
 * Parse `URLSearchParams` from the embed route (`/map?mode=embed&...`)
 * into a list of LocationPin. Returns [] when start params are missing
 * or fail validation; returns just the start pin if the additional
 * `pins` array fails to parse or validate.
 *
 * Param shape matches the output of `mapLinkHref` validator:
 *   postId    — string identifier of the start pin's post
 *   latitude  — numeric, -90..90
 *   longitude — numeric, -180..180
 *   pins      — JSON-encoded array (URLSearchParams handles URL decoding):
 *               [{ postId: uuid, latitude, longitude }, ...]
 *
 * Note: start `postId` is just `string().min(1)` because the producer's
 * `mapLinkSchema.startPosition.postId` is `z.string().optional()` — the
 * pin elements require uuid format but the start does not. Asymmetry
 * preserved here.
 */
function readNumericParam(value: string | null): number | undefined {
  if (value === null || value.trim() === '') return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
}

export function parseEmbedPins(
  searchParams: URLSearchParams | null
): LocationPin[] {
  if (!searchParams) return [];

  const startResult = startPinSchema.safeParse({
    postId: searchParams.get('postId'),
    latitude: readNumericParam(searchParams.get('latitude')),
    longitude: readNumericParam(searchParams.get('longitude'))
  });
  if (!startResult.success) return [];

  const startPin: LocationPin = { ...startResult.data, factiiiId: null };

  const pinsRaw = searchParams.get('pins');
  if (!pinsRaw) return [startPin];

  try {
    const parsed = pinsArraySchema.parse(JSON.parse(pinsRaw));
    // Dedupe by postId+lat+lng. Callers (including mobile
    // PostMapWebView) often include the start pin in `pins` too, and
    // Cesium throws on duplicate entity ids.
    const seen = new Set<string>();
    const all: LocationPin[] = [];
    for (const p of [startPin, ...parsed]) {
      const key = `${p.postId}|${p.latitude}|${p.longitude}`;
      if (seen.has(key)) continue;
      seen.add(key);
      all.push({ ...p, factiiiId: null });
    }
    return all;
  } catch {
    return [startPin];
  }
}
